import { chakra, useCheckbox, Box } from "@chakra-ui/react";
import { createContext, useContext } from "react";
import { HiCheck, HiX } from "react-icons/hi";

// Context to share state between components
const SwitchContext = createContext();

// 1. Root Component
const Root = ({ children, ...props }) => {
  const { state, getInputProps, getLabelProps, htmlProps } = useCheckbox(props);

  return (
    <SwitchContext.Provider
      value={{ isChecked: state.isChecked, isFocused: state.isFocused }}
    >
      <chakra.label
        {...htmlProps}
        cursor="pointer"
        display="inline-flex"
        alignItems="center"
        position="relative"
        verticalAlign="middle"
      >
        <input {...getInputProps({}, { isChecked: state.isChecked })} hidden />
        {children}
      </chakra.label>
    </SwitchContext.Provider>
  );
};

// 2. Hidden Input (Handled in Root, placeholder for compatibility)
const HiddenInput = (props) => null;

// 3. Control (Track)
const Control = ({ children, ...props }) => {
  const { isChecked } = useContext(SwitchContext);
  return (
    <chakra.span
      display="inline-flex"
      alignItems="center"
      justifyContent="flex-start"
      flexShrink={0}
      bg={isChecked ? "blue.500" : "gray.200"}
      borderRadius="full"
      p="2px"
      width="var(--switch-track-width, 3.25rem)"
      height="var(--switch-track-height, 1.75rem)"
      transition="all 0.2s"
      _hover={{ bg: isChecked ? "blue.600" : "gray.300" }}
      {...props}
    >
      {children}
    </chakra.span>
  );
};

// 4. Thumb
const Thumb = ({ children, ...props }) => {
  const { isChecked } = useContext(SwitchContext);
  return (
    <chakra.span
      bg="white"
      borderRadius="full"
      boxShadow="sm"
      width="var(--switch-thumb-size, 1.5rem)"
      height="var(--switch-thumb-size, 1.5rem)"
      transition="transform 0.2s"
      transform={
        isChecked
          ? "translateX(var(--switch-thumb-x, 1.5rem))"
          : "translateX(0)"
      }
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="black"
      {...props}
    >
      {children}
    </chakra.span>
  );
};

// 5. Thumb Indicator (Icon switcher)
const ThumbIndicator = ({ children, fallback }) => {
  const { isChecked } = useContext(SwitchContext);
  return isChecked ? children : fallback;
};

// 6. Label
const Label = ({ children, ...props }) => {
  return (
    <chakra.span marginStart="0.5rem" {...props}>
      {children}
    </chakra.span>
  );
};

// Bundle components to match the requested API
const Switch = { Root, HiddenInput, Control, Thumb, ThumbIndicator, Label };

export default function CustomSwitch({ name, isChecked, onChange }) {
  return (
    <Switch.Root
      size="md"
      isChecked={isChecked}
      onChange={onChange}
      name={name}
    >
      <Switch.HiddenInput />
      <Switch.Control>
        <Switch.Thumb>
          <Switch.ThumbIndicator fallback={<HiX color="black" />}>
            <HiCheck />
          </Switch.ThumbIndicator>
        </Switch.Thumb>
      </Switch.Control>
      <Switch.Label>{isChecked ? "Active" : "Inactive"}</Switch.Label>
    </Switch.Root>
  );
}
